<template>
  <div v-if="isLoading()" class="loader center-middle">
    <LoadingState :size="50" />
  </div>
  <div v-else-if="isErrored()">
    <ErrorState />
  </div>
  <form v-else @submit.prevent="submit">
    <template v-if="hasStagingEnvironments(stagingEnvironments)">
      <p v-if="isShared()">{{ $t("global.safe-harbor-removing-with-staging") }}</p>
      <div v-else>
        <p>{{ $t("global.safe-harbor-plan-removing-with-staging") }}</p>
        <div class="py-3">
          <p v-for="environment in stagingEnvironments" class="text-center" :key="environment.id">
            {{ environment.cloudAccount.domain }}
          </p>
        </div>
        <Alert
          v-if="!fields.upgradeStaging"
          :message="$t('global.safe-harbor-plan-staging-delete')"
          variant="warning"
          :dismissible="false"
        />
        <FormCheckbox
          name="stagingAction"
          v-model="fields.upgradeStaging"
          :label="`${$t('global.safe-harbor-plan-staging-upgrade', {
            cost: getUpgradeMonthlyCost(),
            domain: getPlanPrimaryDomain(),
          })}`"
        />
      </div>
    </template>
    <p v-if="!hasStagingEnvironments(stagingEnvironments) || isShared()">
      {{ $t("global.safe-harbor-remove-confirm") }}
    </p>
    <div class="form-buttons">
      <Btn
        class="mr-2"
        variant="light"
        type="button"
        :disabled="isSubmitting"
        @click="cancel"
        data-test="cancel-remove-safe-harbor-button"
      >
        {{ $t("global.cancel") }}
      </Btn>
      <Btn
        variant="danger"
        type="submit"
        :disabled="!isSubmittable()"
        :loading="isSubmitting"
        data-test="remove-safe-harbor-button"
      >
        {{ $t("global.remove") }}
      </Btn>
    </div>
  </form>
</template>

<script>
import { computed, onMounted, reactive, ref } from "@vue/composition-api";
import Alert from "@/components/display/Alert";
import Btn from "@/components/display/Btn";
import ErrorState from "@/components/display/ErrorState";
import FormCheckbox from "@/components/display/form/FormCheckbox";
import LoadingState from "@/components/display/LoadingState";
import store from "@/store";
import useValidator from "@/utils/useValidator";
import { requestHelp as validationRules } from "leatherman-js/utils/validations/forms";

export default {
  name: "SafeHarborRemoveForm",
  props: {
    service: {
      type: Object,
      required: true,
    },
  },
  components: {
    Alert,
    Btn,
    ErrorState,
    FormCheckbox,
    LoadingState,
  },
  setup(props, { emit, root }) {
    const stagingEnvironments = computed(() => {
      if (isShared()) {
        return store.getters["safeHarbor/getStagingEnvironments"];
      } else {
        return store.getters["safeHarbor/getSiteStagingEnvironments"];
      }
    });
    const stagingEnvironmentsStatus = computed(() => {
      if (isShared()) {
        return store.getters["safeHarbor/getStagingEnvironmentsStatus"];
      } else {
        return store.getters["safeHarbor/getSiteStagingEnvironmentsStatus"];
      }
    });
    const pkg = computed(() => store.getters["safeHarbor/getPackage"]);
    const packageStatus = computed(() => store.getters["safeHarbor/getPackageStatus"]);

    const isSubmitting = ref(false);
    const errors = ref(null);
    const fields = reactive({ stagingAction: null, upgradeStaging: false });
    const { validator } = useValidator();
    const validation = validator(
      fields,
      validationRules,
      {},
      {
        budget: {
          required: root.$t("validation.budget-required"),
        },
        description: {
          required: root.$t("validation.description-required"),
        },
      }
    );

    const cancel = () => emit("close");

    const getPlanPrimaryDomain = () => props.service?.cloudAccount?.domain;

    const getStatingActionOptions = ["convert", "delete"];

    const getUpgradeMonthlyCost = () => `$${pkg.value?.monthlyFee}`;

    const hasStagingEnvironments = (stagingServices) => stagingServices.length > 0;

    const isErrored = () =>
      stagingEnvironmentsStatus.value.isErrored || packageStatus.value.isErrored;
    const isLoading = () =>
      stagingEnvironmentsStatus.value.isLoading || packageStatus.value.isLoading;

    const isShared = () => props.service.type === "shared";

    const isSubmittable = () => !isSubmitting.value && validation.errors.length === 0;
    const submit = () => {
      errors.value = null;

      validation
        .validate()
        .then(() => {
          if (hasStagingEnvironments(stagingEnvironments.value) && !isShared()) {
            fields.stagingAction = fields.upgradeStaging ? "convert" : "delete";
          }
          isSubmitting.value = true;
          store
            .dispatch("safeHarbor/remove", {
              serviceId: props.service.id,
              addonId: props.service.package.safeHarbor.id,
              data: {
                stagingAction: fields.stagingAction,
              },
            })
            .then(() => {
              if (isShared()) {
                store.dispatch("safeHarbor/addUpdate", props.service.id);
              }
              store.dispatch("toasts/addToast", {
                variant: "info",
                message: root.$t("toasts.safe-harbor-removing-message"),
              });
              emit("close");
            })
            .catch(() => {
              store.dispatch("toasts/addToast", {
                variant: "error",
                message: root.$t("toasts.safe-harbor-remove-error-message", {
                  name: props.planName,
                }),
              });
            })
            .finally(() => {
              isSubmitting.value = false;
            });
        })
        .catch(() => {});
    };

    onMounted(() => {
      if (isShared()) {
        store.dispatch("safeHarbor/getStagingEnvironments", props.service.id).then((response) => {
          if (hasStagingEnvironments(response.list)) {
            fields.stagingAction = "delete";
          }
        });
      } else {
        store
          .dispatch("safeHarbor/getSiteStagingEnvironments", props.service.cloudAccount.id)
          .then((response) => {
            if (hasStagingEnvironments(response.list)) {
              store.dispatch("safeHarbor/getPackage", props.service.package.label);
            }
          });
      }
    });

    return {
      cancel,
      errors,
      fields,
      getPlanPrimaryDomain,
      getStatingActionOptions,
      getUpgradeMonthlyCost,
      hasStagingEnvironments,
      isErrored,
      isLoading,
      isShared,
      isSubmittable,
      isSubmitting,
      pkg,
      stagingEnvironments,
      submit,
      validation,
    };
  },
};
</script>

<style lang="scss" scoped>
@import "~@/styles/variables";
.form-buttons {
  text-align: right;
  padding-top: 2rem;
}
</style>
